import React, { useState, useEffect, useMemo } from 'react'; 
import { User, Deal, Activity } from '../types';
import { 
  StickyNote, Send, RefreshCw, Clock, Search, X,
  MessageSquare, Users, Tag, ShieldCheck
} from 'lucide-react'; 
import { crmDataService } from '../services/crmDataService';

interface ActivityLogProps {
  user: User;
  deals: Deal[];
  onAddActivity: (activity: any) => void;
  globalRefreshKey?: number;
}

const ActivityLog: React.FC<ActivityLogProps> = ({ user, deals, onAddActivity, globalRefreshKey = 0 }) => {
  // ============================================================================
  // 1. 상태 관리
  // ============================================================================
  const [selectedDealId, setSelectedDealId] = useState('');
  const [logType, setLogType] = useState<'Meeting' | 'Call' | 'Email' | 'Note'>('Meeting');
  const [customerGrade, setCustomerGrade] = useState<'S' | 'A' | 'B' | 'C' | ''>('');
  const [isCsActivity, setIsCsActivity] = useState(false);
  const [notes, setNotes] = useState('');
  const [projectSearch, setProjectSearch] = useState(''); 
  
  const [isSaving, setIsSaving] = useState(false);
  const [timelineLogs, setTimelineLogs] = useState<any[]>([]);

  // ============================================================================
  // 2. 검색 및 필터 로직 (금액 표시 유지)
  // ============================================================================
  const filteredDeals = useMemo(() => {
    if (!projectSearch.trim()) return deals;
    return deals.filter((d: any) => 
      (d['거래처'] || '').toLowerCase().includes(projectSearch.toLowerCase()) ||
      (d['건명'] || '').toLowerCase().includes(projectSearch.toLowerCase())
    );
  }, [deals, projectSearch]);

  const myDeals = filteredDeals.filter((d: any) => d['담당자'] === user.name);
  const otherDeals = filteredDeals.filter((d: any) => d['담당자'] !== user.name);

  // ============================================================================
  // 3. 데이터 로드 및 저장 핸들러
  // ============================================================================
  useEffect(() => {
    if (!selectedDealId) {
      setTimelineLogs([]);
      return;
    }
    const fetchLiveHistory = async () => {
      try {
        const data = await crmDataService.getActivityHistory(selectedDealId);
        if (data && Array.isArray(data)) setTimelineLogs(data);
        else if (data && data.history) setTimelineLogs(data.history);
      } catch (error) {
        console.error("히스토리 로드 실패:", error);
      }
    };
    fetchLiveHistory();
  }, [selectedDealId, globalRefreshKey]); // selectedDealId 또는 globalRefreshKey가 변경될 때마다 히스토리 새로고침

  const handleSaveOnly = async () => {
    if (!selectedDealId || !notes.trim()) return;
    setIsSaving(true);
    
    try {
      const gradeTag = customerGrade ? `[${customerGrade}급]` : '';
      const csTag = isCsActivity ? `[CS지원]` : '';
      const finalDescription = `${csTag} ${gradeTag} ${notes}`.trim();

      const newActivity = {
        deal_id: selectedDealId,
        activity_type: logType,
        memo: finalDescription,
        last_editor: user.name,
        last_meet: new Date().toISOString().split('T')[0]
      };

      await crmDataService.updateSalesActivity(selectedDealId, newActivity);
      
      const data = await crmDataService.getActivityHistory(selectedDealId);
      if (data && Array.isArray(data)) setTimelineLogs(data);
      else if (data && data.history) setTimelineLogs(data.history);
      
      onAddActivity({ editor: user.name, content: finalDescription, created_at: new Date().toISOString() });
      
      setNotes('');
      setCustomerGrade('');
      setIsCsActivity(false);
    } catch (error) {
      alert("기록 저장 중 오류가 발생했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================================================
  // 4. UI 렌더링
  // ============================================================================
  return (
    <div className="flex flex-col gap-8 animate-fade-in pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* [좌측] 활동 입력 (원본 유지) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white p-7 rounded-[40px] shadow-2xl border border-blue-100 flex flex-col gap-6 sticky top-6">
            <div className="flex items-center gap-3 border-l-8 border-indigo-600 pl-4">
              <StickyNote className="text-indigo-600" size={28} strokeWidth={3} />
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">활동 로그 작성</h3>
            </div>

            <div className="space-y-5">
              <div className="space-y-3">
                <label className="block text-[11px] font-black text-indigo-500 uppercase flex items-center gap-1 tracking-wider">
                  <Tag size={12}/> STEP 1. 프로젝트 검색 및 선택
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={16} strokeWidth={3} />
                  <input 
                    type="text"
                    placeholder="거래처 또는 건명으로 찾기..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    className="w-full pl-11 pr-12 py-3 bg-indigo-50/50 border-2 border-indigo-100 rounded-2xl text-sm font-black focus:border-indigo-500 focus:bg-white outline-none transition-all placeholder:text-indigo-200"
                  />
                  {projectSearch && (
                    <button onClick={() => setProjectSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-300 hover:text-indigo-600 transition-colors">
                      <X size={18} strokeWidth={3} />
                    </button>
                  )}
                </div>

                <select 
                  value={selectedDealId}
                  onChange={(e) => setSelectedDealId(e.target.value)}
                  className="w-full p-4 bg-white border-2 border-slate-100 rounded-2xl text-sm font-black focus:border-blue-500 outline-none text-slate-800 shadow-sm"
                >
                  <option value="">{projectSearch ? `검색 결과: ${filteredDeals.length}건` : "대상 프로젝트를 선택하세요"}</option>
                  {myDeals.length > 0 && (
                    <optgroup label="⭐ 내 담당 프로젝트">
                      {myDeals.map((d: any) => (
                        <option key={d.id} value={d.id}>
                          [{d['거래처']}] {d['건명']} (₩{Number(d['소계'] || 0).toLocaleString()})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {otherDeals.length > 0 && (
                    <optgroup label="🏢 기타 프로젝트 (전체)">
                      {otherDeals.map((d: any) => (
                        <option key={d.id} value={d.id}>
                          [{d['거래처']}] {d['건명']} (₩{Number(d['소계'] || 0).toLocaleString()})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100 space-y-4 shadow-inner">
                  <div>
                    <label className="block text-[11px] font-black text-slate-400 uppercase mb-3 flex items-center gap-1"><Users size={12}/> STEP 2. 고객사 미팅 상대 등급</label>
                    <div className="grid grid-cols-4 gap-2">
                        {[{ id: 'S', desc: '의사결정', color: 'bg-purple-600' }, { id: 'A', desc: '중간관리', color: 'bg-blue-600' }, { id: 'B', desc: '실무담당', color: 'bg-emerald-600' }, { id: 'C', desc: '현장사용', color: 'bg-orange-600' }].map(g => (
                          <button 
                            key={g.id}
                            onClick={() => setCustomerGrade(customerGrade === g.id ? '' : g.id as any)}
                            className={`flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all ${customerGrade === g.id ? `${g.color} border-white text-white shadow-xl scale-105` : 'bg-white border-slate-100 text-slate-400 hover:border-indigo-200'}`}
                          >
                            <span className="font-black text-base">{g.id}</span>
                            <span className="text-[9px] font-bold mt-0.5 opacity-80">{g.desc}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${isCsActivity ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                        <ShieldCheck size={20} strokeWidth={3} />
                      </div>
                      <span className={`text-sm font-black ${isCsActivity ? 'text-green-700' : 'text-slate-500'}`}>CS팀 기술 지원 / 방문</span>
                    </div>
                    <button onClick={() => setIsCsActivity(!isCsActivity)} className={`w-14 h-7 rounded-full transition-all relative ${isCsActivity ? 'bg-green-500 shadow-lg shadow-green-100' : 'bg-slate-300'}`}>
                      <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${isCsActivity ? 'left-8' : 'left-1'}`}></div>
                    </button>
                  </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-slate-400 uppercase mb-2 flex items-center gap-1"><MessageSquare size={12}/> STEP 3. 상세 활동 브리핑</label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="미팅에서 논의된 핵심 내용, 요구사항 등을 기록하세요."
                  className="w-full h-40 p-5 bg-white border-2 border-slate-100 rounded-[30px] text-sm font-medium resize-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none leading-relaxed shadow-sm transition-all"
                />
              </div>
            </div>

            <button 
              onClick={handleSaveOnly}
              disabled={isSaving || !selectedDealId || !notes.trim()}
              className="w-full bg-slate-900 text-white py-5 rounded-[24px] font-black text-base flex items-center justify-center gap-3 hover:bg-indigo-600 disabled:opacity-30 transition-all shadow-2xl shadow-indigo-100 mt-2 active:scale-95"
            >
              {isSaving ? <RefreshCw size={20} className="animate-spin" /> : <Send size={20} strokeWidth={3} />}
              {isSaving ? '서버 저장 중...' : '활동 기록 확정 및 저장'}
            </button>
          </div>
        </div>

        {/* [우측] 통합 활동 히스토리 (수정된 디자인 적용) */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          <div className="bg-white rounded-[40px] shadow-xl border border-slate-100 p-8 flex flex-col gap-6 flex-1 min-h-[500px]">
             <div className="flex items-center justify-between border-b-2 border-slate-50 pb-5">
               <div className="flex items-center gap-3 border-l-8 border-slate-900 pl-4">
                 <Clock className="text-slate-900" size={24} strokeWidth={3} />
                 <h3 className="text-xl font-black text-slate-900 tracking-tight">프로젝트 히스토리</h3>
               </div>
               <span className="text-xs font-bold text-slate-400">총 {timelineLogs.length}건의 기록</span>
             </div>

             <div className="space-y-0 overflow-y-auto pr-2 custom-scrollbar">
                {timelineLogs.length === 0 ? (
                  <div className="py-24 text-center flex flex-col items-center justify-center">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                      <MessageSquare size={40} className="text-slate-200"/>
                    </div>
                    <p className="text-sm font-black text-slate-300">선택된 프로젝트의 기록이 아직 없습니다.</p>
                  </div>
                ) : (
                  timelineLogs.map((log, i) => {
                    const editorName = log.editor || log.createdBy || '관리자';
                    const contentStr = log.content || log.description || '';
                    const dateStr = log.created_at || log.date || '';
                    
                    return (
                      <div key={i} className="relative pl-7 pb-8 group">
                        {/* 타임라인 선 및 포인트 */}
                        <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200 group-hover:bg-indigo-400 transition-colors"></div>
                        <div className="absolute left-[-4px] top-2 w-2 h-2 rounded-full bg-slate-300 group-hover:bg-indigo-600 transition-colors shadow-sm"></div>

                        {/* 상단 정보: 건명, 등급, 이름, 날짜 */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {contentStr.match(/^\[.*?\]/) && (
                            <span className="text-[11px] font-black text-indigo-600 px-1.5 py-0.5 border border-indigo-200 rounded-md bg-indigo-50/50">
                              {contentStr.match(/^\[.*?\]/)[0].replace(/[\[\]]/g, '')}
                            </span>
                          )}
                          {contentStr.match(/\[([SABC]급)\]/) && (
                            <span className="text-[10px] font-black text-emerald-600 px-1.5 py-0.5 border border-emerald-200 rounded-md bg-emerald-50/50">
                              {contentStr.match(/\[([SABC]급)\]/)[1]} 등급
                            </span>
                          )}
                          <span className="text-slate-300 mx-1 text-xs">|</span>
                          <span className="text-sm font-black text-slate-800 tracking-tight">{editorName}</span>
                          <span className="text-[10px] text-slate-400 font-bold ml-auto bg-slate-100 px-2 py-0.5 rounded-full">
                            {dateStr ? new Date(dateStr).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '방금 전'}
                          </span>
                        </div>

                        {/* 본문 박스 */}
                        <div className="text-[14px] text-slate-700 leading-relaxed font-medium pl-1 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm group-hover:border-indigo-100 transition-all">
                          <p className="whitespace-pre-wrap">
                            {contentStr.replace(/^\[.*?\]/, '').replace(/\[[SABC]급\]/g, '').replace(/\[CS지원\]/g, '').trim()}
                          </p>
                          
                          {contentStr.includes('[CS지원]') && (
                            <div className="mt-2 text-[10px] font-black text-rose-500 italic">
                              # CS 기술지원 완료
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
//테스트